import React, { useMemo } from "react";

import {
  faCog,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { startCase } from "lodash-es";
import { Link } from "react-router-dom";

import {
  computeModelUsageDrift,
  MODEL_USAGE_FIELD_LABELS,
  ModelUsageDrift,
} from "src/adminPanel/modelQuotas/drift";
import { SortableFilterHeader } from "src/adminPanel/SortableFilterHeader";
import { Module } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Cell, TableComp } from "src/base-components/Table";
import {
  FlowApiSchemasModelUsageFeatureOverride,
  FlowApiSchemasModelUsageTier,
  WorkspaceConfig,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export type ModelQuotaRow = {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  orgId: string;
  orgName: string;
  tier: FlowApiSchemasModelUsageTier;
  enabledFeatures: string[];
  agentsModuleId: string | undefined;
  awsAccountName: string | undefined;
  workspace: WorkspaceDataplane;
  drift: ModelUsageDrift;
};

const TIER_PILL_VARIANT: Record<FlowApiSchemasModelUsageTier, PillVariants> = {
  [FlowApiSchemasModelUsageTier.DEFAULT]: "gray",
  [FlowApiSchemasModelUsageTier.PREMIUM]: "indigo-light",
  [FlowApiSchemasModelUsageTier.CUSTOM]: "orange",
};

const TIER_LABEL: Record<FlowApiSchemasModelUsageTier, string> = {
  [FlowApiSchemasModelUsageTier.DEFAULT]: "Default",
  [FlowApiSchemasModelUsageTier.PREMIUM]: "Premium",
  [FlowApiSchemasModelUsageTier.CUSTOM]: "Custom",
};

const TIER_FILTER_OPTIONS = Object.values(FlowApiSchemasModelUsageTier).map(
  (tier) => ({ value: tier, label: TIER_LABEL[tier] }),
);

const NO_FEATURES_FILTER = "__none__";

const AGENTS_MODULE_FILTER_OPTIONS = [
  { value: "deployed", label: "Deployed" },
  { value: "not_deployed", label: "Not deployed" },
];

const getEnabledFeatures = (config: WorkspaceConfig): string[] => {
  const featureOverrides = (config.model_usage_feature_overrides ??
    {}) as Record<string, FlowApiSchemasModelUsageFeatureOverride>;
  return Object.entries(featureOverrides)
    .filter(([, override]) => override.enabled)
    .map(([key]) => key)
    .sort();
};

export const buildModelQuotaRows = (
  workspaces: WorkspaceDataplane[],
  orgMap: Map<string, string>,
  agentsModuleByWorkspaceId: Map<string, Module>,
  awsAccountNameByUuid: Map<string, string>,
): ModelQuotaRow[] =>
  workspaces.map((ws) => {
    const config = (ws.config ?? {}) as WorkspaceConfig;
    const agentsModule = agentsModuleByWorkspaceId.get(ws.id);
    return {
      workspaceId: ws.id,
      workspaceName: ws.name,
      workspaceSlug: ws.slug,
      orgId: ws.organization_id,
      orgName: orgMap.get(ws.organization_id) ?? "",
      tier: config.model_usage_tier ?? FlowApiSchemasModelUsageTier.DEFAULT,
      enabledFeatures: getEnabledFeatures(config),
      agentsModuleId: agentsModule?.id,
      awsAccountName: ws.aws_account_id
        ? awsAccountNameByUuid.get(ws.aws_account_id)
        : undefined,
      workspace: ws,
      drift: computeModelUsageDrift(config, agentsModule),
    };
  });

const columnHelper = createColumnHelper<ModelQuotaRow>();

type Props = {
  rows: ModelQuotaRow[];
  onEdit: (row: ModelQuotaRow) => void;
};

export const ModelQuotasTable: React.FC<Props> = ({ rows, onEdit }) => {
  const featureFilterOptions = useMemo(() => {
    const featureKeys = [
      ...new Set(rows.flatMap((row) => row.enabledFeatures)),
    ].sort();
    return [
      { value: NO_FEATURES_FILTER, label: "No features" },
      ...featureKeys.map((key) => ({ value: key, label: startCase(key) })),
    ];
  }, [rows]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.orgName || row.orgId, {
        id: "organization",
        header: (info) => (
          <SortableFilterHeader info={info} showFilter>
            Organization
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <span className="inline-flex items-center gap-1">
                <Link
                  className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                  to={`/admin-panel/organizations?search=${row.orgId}`}
                >
                  <span className="font-inter-normal-13px">
                    {row.orgName || row.orgId.slice(0, 8)}
                  </span>
                </Link>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy organization ID"
                  value={row.orgId}
                />
              </span>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
      }),
      columnHelper.accessor((row) => row.workspaceName, {
        id: "workspace",
        header: (info) => (
          <SortableFilterHeader info={info} showFilter>
            Workspace
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <div className="flex items-center gap-1">
                <div className="flex flex-col">
                  <Link
                    className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                    to={`/admin-panel/organizations?search=${row.workspaceId}`}
                  >
                    <span className="font-inter-normal-13px">
                      {row.workspaceName}
                    </span>
                  </Link>
                  <span className="font-inter-normal-12px text-gray-500">
                    {row.workspaceSlug}
                  </span>
                </div>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy workspace ID"
                  value={row.workspaceId}
                />
              </div>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) => {
          const lower = filterValue.toLowerCase();
          const r = row.original;
          return (
            r.workspaceName.toLowerCase().includes(lower) ||
            r.workspaceSlug.toLowerCase().includes(lower) ||
            r.workspaceId.toLowerCase().includes(lower)
          );
        },
      }),
      columnHelper.accessor((row) => row.tier, {
        id: "tier",
        header: (info) => (
          <SortableFilterHeader filterOptions={TIER_FILTER_OPTIONS} info={info}>
            Model tier
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const tier = info.getValue();
          return (
            <Cell className="px-4" info={info}>
              <Pill variant={TIER_PILL_VARIANT[tier]}>
                <Pill.Text>{TIER_LABEL[tier]}</Pill.Text>
              </Pill>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) =>
          row.original.tier === filterValue,
      }),
      columnHelper.accessor((row) => row.enabledFeatures.join(", "), {
        id: "features",
        header: (info) => (
          <SortableFilterHeader
            filterOptions={featureFilterOptions}
            info={info}
          >
            Features
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const enabledFeatures = info.row.original.enabledFeatures;
          return (
            <Cell className="px-4" info={info}>
              {enabledFeatures.length === 0 ? (
                <span className="font-inter-normal-13px text-gray-500">—</span>
              ) : (
                <div className="flex flex-wrap items-center gap-1">
                  {enabledFeatures.map((feature) => (
                    <Pill key={feature} variant="green">
                      <Pill.Text>{startCase(feature)}</Pill.Text>
                    </Pill>
                  ))}
                </div>
              )}
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) =>
          filterValue === NO_FEATURES_FILTER
            ? row.original.enabledFeatures.length === 0
            : row.original.enabledFeatures.includes(filterValue),
      }),
      columnHelper.accessor((row) => row.agentsModuleId !== undefined, {
        id: "agentsModule",
        header: (info) => (
          <SortableFilterHeader
            filterOptions={AGENTS_MODULE_FILTER_OPTIONS}
            info={info}
          >
            Agents module
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const deployed = info.getValue();
          const drift = info.row.original.drift;
          return (
            <Cell className="px-4" info={info}>
              <div className="flex items-center gap-2">
                <Pill variant={deployed ? "green" : "gray"}>
                  <Pill.Text>
                    {deployed ? "Deployed" : "Not deployed"}
                  </Pill.Text>
                </Pill>
                {drift.hasDrift && (
                  <Tooltip
                    asChild
                    title={`Workspace config differs from agents module on: ${drift.fields
                      .map((f) => MODEL_USAGE_FIELD_LABELS[f])
                      .join(", ")}`}
                  >
                    <span
                      className="inline-flex"
                      data-loc={`drift-warning-${info.row.original.workspaceId}`}
                    >
                      <Icon
                        icon={faTriangleExclamation}
                        size="sm"
                        variant="warning"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            </Cell>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) =>
          (row.original.agentsModuleId !== undefined) ===
          (filterValue === "deployed"),
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-10 px-4 py-3" />,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <Button
              aria-label="Edit model quotas"
              dataLoc={`edit-quota-${info.row.original.workspaceId}`}
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(info.row.original);
              }}
            >
              <Icon icon={faCog} size="sm" />
            </Button>
          </Cell>
        ),
        meta: { displayName: "Actions", sticky: "right" as const },
      }),
    ],
    [onEdit, featureFilterOptions],
  );

  return (
    <div className="flex max-h-[calc(100vh-340px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <TableComp
        clientSideFiltering
        columns={columns}
        data={rows}
        dataLoc="model-quotas-table"
        disableVirtualization
        getRowId={(row) => row.workspaceId}
        variant="default"
      />
    </div>
  );
};
